// Server-write ledger for field app responses (card #4688).
//
// GOAL: logging only -- this module never changes what gets sent to the
// server, never changes survey rendering, and never changes autosave
// behaviour. It exists so a Tasker's "my completed work vanished on re-open"
// report can be traced after the fact: what actually happened to each server
// write on a response (start, complete, schedule, answers sync, photo link),
// in order, without ever holding an answer, a photo, or any personal data.
//
// Two things happen on every write:
//   1. One app_log entry (via field_logger) -- action, local status
//      before/after, HTTP status, server-returned status, X-Request-Id.
//   2. One row appended to the `response_ledger` IndexedDB table, capped at
//      LEDGER_CAP rows per response (oldest dropped first) -- a short recent
//      trail, not a full history. Lets a support trace, or a Playwright spec,
//      read back the sequence of writes for one response without scraping
//      the shared app_log (which mixes in every other category).
//
// A note on "server-returned status": a redirecting action (start, complete)
// is always followed to its destination by fetch's default redirect:
// "follow" -- the response JS actually sees is the landed page's, not the
// write action's own. Rails only ever sets the X-WIN-Response-Status header
// on the write action itself (see Field::ResponsesController,
// Api::V1::Surveys::ResultsController), so it is genuinely absent (not
// wrong) on a followed redirect -- callers for those two actions pass
// `requestId: null` explicitly rather than attribute the LANDED page's
// request id to the write that redirected to it.

import db from "./field_db"
import { logEvent, logWarn } from "./field_logger"

// A short recent trail per response, not a full history (card #4688).
const LEDGER_CAP = 20

export const ENDED_STATUS = "ended"

export const ACTIONS = Object.freeze({
  START:        "start",
  COMPLETE:     "complete",
  SCHEDULE:     "schedule",
  ANSWERS_SYNC: "answers_sync",
  PHOTO_LINK:   "photo_link"
})

// Records one server-write ledger entry for `responseId`: appends the capped
// IndexedDB row, mirrors it to the app diagnostic log, and -- if the ledger's
// last known status for this response was "ended" but this write's own
// server-returned status says otherwise -- warns "Response status reverted"
// (both values). Never throws: ledger bookkeeping must never break the real
// write path it is attached to.
export async function recordLedgerEntry(responseId, {
  action,
  localStatusBefore = null,
  localStatusAfter  = null,
  httpStatus        = null,
  serverStatus      = null,
  requestId         = null
} = {}) {
  const id = Number(responseId)
  if (!id || !action) return null

  const priorKnown = await lastKnownStatus(id).catch(() => null)

  const entry = {
    responseId: id,
    action,
    localStatusBefore,
    localStatusAfter,
    httpStatus,
    serverStatus,
    requestId,
    loggedAt: new Date().toISOString()
  }

  try {
    await db.response_ledger.add(entry)
    await _trim(id)
  } catch { /* bookkeeping only -- never break the write it is attached to */ }

  await logEvent("ledger", "Response write recorded", entry).catch(() => {})

  if (priorKnown === ENDED_STATUS && serverStatus && serverStatus !== ENDED_STATUS) {
    await logWarn("ledger", "Response status reverted", {
      responseId:     id,
      action,
      ledgerStatus:   priorKnown,
      observedStatus: serverStatus
    }).catch(() => {})
  }

  return entry
}

// Oldest-first rows for one response. Deliberately NOT sorted by loggedAt
// (two entries written in the same tight loop/millisecond would tie) --
// `responseId` is a non-unique index, and IndexedDB guarantees a cursor over
// one returns matches ordered by primary key (`id`, auto-increment) for equal
// index values, which is exactly insertion order here.
async function _orderedRows(responseId) {
  return db.response_ledger.where("responseId").equals(responseId).toArray()
}

async function _trim(responseId) {
  const rows = await _orderedRows(responseId)
  const excess = rows.length - LEDGER_CAP
  if (excess > 0) {
    await db.response_ledger.bulkDelete(rows.slice(0, excess).map(r => r.id))
  }
}

// The most recent known status for a response, from its own ledger -- the
// latest entry's server-returned status, falling back to its local status
// after (a redirecting write with no visible server status, see header
// comment). null when the ledger holds nothing for this response yet.
export async function lastKnownStatus(responseId) {
  const rows = await _orderedRows(Number(responseId))
  const last = rows[rows.length - 1]
  return last ? (last.serverStatus ?? last.localStatusAfter ?? null) : null
}

// Every ledger row for one response, oldest first -- used by support tracing
// and by tests asserting the write sequence.
export async function ledgerEntriesFor(responseId) {
  return _orderedRows(Number(responseId))
}

// Warns when a READ (a sync's response body, or a plain screen load/render)
// observes a non-ended status for a response the ledger last recorded as
// ended -- the read-side counterpart to the write-side check inside
// recordLedgerEntry above ("a later sync OR screen load", card #4688 notes).
// Does not itself write a ledger row -- a render is not a server write.
export async function checkRenderedStatusForReversion(responseId, observedStatus, context = {}) {
  if (!observedStatus) return
  const known = await lastKnownStatus(responseId).catch(() => null)
  if (known === ENDED_STATUS && observedStatus !== ENDED_STATUS) {
    await logWarn("ledger", "Response status reverted", {
      responseId:     Number(responseId),
      ledgerStatus:   known,
      observedStatus,
      ...context
    }).catch(() => {})
  }
}
